import {
  createSearchParams,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import AppLayout from './components/AppLayout';

import MyMerchantsView from './views/MerchantConnect/MyMerchantsView';
import ForgotPasswordFormView from './views/ForgotPasswordFormView';
import LoginFormView from './views/LoginFormView';
import { Empty } from 'fave-ui';
import {
  RedirectToAuthenticatedPagesView,
  RedirectToLoginPageView,
} from './views/RedirectView/RedirectView';
import { ProductDashboardView } from './views/ProductDashboardView/ProductDashboardView';
import { NatureOfBusinessView } from './views/NatureOfBusinessView';
import { CreateNatureOfBusinessView } from './views/NatureOfBusinessView/CreateNatureOfBusiness';
import { MasterDashboardView } from './views/MasterDashboardView';
import MasterContractView from './views/MasterDashboardView/MasterContractView';
import PaymentModeConfigurationFormView from './views/PaymentModeView';
import { useTenantContext } from './contexts/TenantContext';
import ApplicationFeeView from './views/PaymentModeView/ApplicationFee';
import ApplicationPaymentModeView from './views/PaymentModeView/ApplicationPaymentMode';
import CampaignManagementView from './views/CampaignManagementView';
import CampaignChangeLogView from './views/CampaignManagementView/CampaignChangeLogView';
import CreateUpdateIPPView from './views/IPPView/CreateUpdateIPPView';
import IPPConfigurationView from './views/IPPView/IPPConfigurationView';
import useOnceCallback from './hooks/useOnceCallback';
import OutletSettingsView from './views/OutletSettings';
import TicketCenterView from './views/FaveArcadeView/TicketCenterView';
import CreateNewCampaignView from './views/FaveArcadeView/TicketCenterView/CreateNewCampaignView';
import TicketChangelogView from './views/FaveArcadeView/TicketCenterView/TicketChangelogView';
import TicketDetailsView from './views/FaveArcadeView/TicketCenterView/TicketDetailsView';
import GamesCenterView from './views/FaveArcadeView/GamesCenterView';
import CreateNewGameCampaignView from './views/FaveArcadeView/GamesCenterView/CreateNewGameCampaign';
import GameChangelogView from './views/FaveArcadeView/GamesCenterView/GameCampaignChangelogView';
import GameDetailsView from './views/FaveArcadeView/GamesCenterView/GameDetailsView';
import PrizeCenterView from './views/FaveArcadeView/PrizeCatalogView';
import CreateNewPrizeView from './views/FaveArcadeView/PrizeCatalogView/CreateNewPrizeView';
import PrizeCampaignChangelogView from './views/FaveArcadeView/PrizeCatalogView/PrizeCampaignChangelogView';
import PrizeDetailsView from './views/FaveArcadeView/PrizeCatalogView/PrizeDetailsView';

import MySubmissionView from './views/MySubmissionView';
import FavePaySubmissionsDetailsView from './views/MySubmissionView/FavepayTab/FavePaySubmissionsDetailsView';
import DealsSubmissionsDetailsView from './views/MySubmissionView/DealsTab/DealsDetailsView';
import ECardSubmissionsDetailsView from './views/MySubmissionView/ECardsTab/ECardDetailsView';
import InstallationPaymentPlanDetailsView from './views/MySubmissionView/InstalmentPaymentPlanTab/InstallationPaymentPlanDetailsView';
import MakerCheckerView from './views/MakerCheckerView';
import CashbackCampaignsView from './views/CashbackCampaignsView';
import GroupFormView from './views/MakerCheckerView/MakerCheckerGroup/GroupFormView';
import CashbackCampaignChangeLogView from './views/CashbackCampaignsView/ChangelogView';
import NewCashbackCampaign from './views/CashbackCampaignsView/CreateCampaignView';
import EditFLPCashbackCampaign from './views/CashbackCampaignsView/FLPCashbackView/EditFLPCashbackCampaign';
import EditFaveCashbackCampaign from './views/CashbackCampaignsView/FaveCashbackView/EditFaveCashbackCampaign';
import PeekyBannersView from './views/PeekyBannersView';
import MerchantSettlementView from './views/MerchantSettlementView';
import EditPrizeCampaign from './views/FaveArcadeView/PrizeCatalogView/EditPrizeCampaign';
import GameCampaignEditView from './views/FaveArcadeView/GamesCenterView/GameCampaignEditView';
import TicketEditView from './views/FaveArcadeView/TicketCenterView/TicketEditView';
import ManualRefund from './views/ManualRefund';
import EditEcardCashbackCampaign from './views/CashbackCampaignsView/ECardGiveawayView/EditEcardCashbackCampaign';
import StatementsView from './views/StatementsView';
import InvoicesView from './views/InvoicesView';
import { FavePassesView } from './views/FavePassesView';
import PaymentModeSubmissionsDetailsView from './views/MySubmissionView/PaymentModeTab/PaymentModeSubmissionsDetailsView';
import FavePassView from './views/FavePassView';
import { FavePassOutletSettingsView } from './views/FavePassesView/OutletSettings';
import FavePassSubmissionsDetailsView from './views/MySubmissionView/FavePassTab/FavePassSubmissionsDetailsView';
import MerchantConnectFavePassForm from './views/MerchantConnect/MerchantConnectFavePassForm';
import { useEnvironmentContext } from './contexts/EnvironmentContext';
import StampCardSubmissionsDetailsView from './views/MySubmissionView/StampCardTab/SubmissionsDetailsView';
import StampCardForm from './views/MerchantConnect/StampCard/StampCardForm';
import UOBPointRedemptionForm from './views/MerchantConnect/UOBPointRedemptionForm';
import MicrosoftCallbackView from './views/MicrosoftCallbackView';

const WithIdParamValidation = ({ Component }: { Component: JSX.Element }) => {
  const data = useParams();

  const idParamsArray = Object.keys(data);
  const validIDParamsArray = idParamsArray.filter(item =>
    data ? parseInt(data[item] as string) > 0 : '',
  );
  const isIDValid = idParamsArray.length === validIDParamsArray.length;

  return isIDValid ? (
    Component
  ) : (
    <Empty
      emptyType={'cant-find-anything'}
      customTitle={'Error. Invalid ID detected.'}
      customDescription={'Please enter valid ID to see page content.'}
    />
  );
};

export const AdminToolRoutes = () => {
  const [queryString] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isID } = useTenantContext();
  const { isProduction } = useEnvironmentContext();

  const isStaging = !isProduction;

  const queryStringObject = Object.fromEntries(queryString);
  const { tenant_code, ...restQueryString } = queryStringObject;
  const shouldRedirect = !!(tenant_code || restQueryString);

  useOnceCallback(() => {
    const redirectLinkWithoutTenantCode = `${location.pathname}?${createSearchParams(restQueryString)}`;

    navigate(redirectLinkWithoutTenantCode, { replace: true });
  }, shouldRedirect);

  return (
    <Routes>
      <Route
        path={'/'}
        element={
          <RedirectToLoginPageView>
            <AppLayout>
              <Outlet />
            </AppLayout>
          </RedirectToLoginPageView>
        }
      >
        <Route
          path={'/'}
          caseSensitive
          element={
            <Navigate to={'/admin/merchant_connect/my_merchant/merchants'} />
          }
        />
        <Route path="operation_dashboard" element={<Outlet />}>
          <Route path="master_dashboard" element={<Outlet />}>
            <Route index element={<MasterDashboardView />} />
            <Route
              path="company/:company_id/mc_submission/:mc_id"
              element={
                <WithIdParamValidation Component={<MasterContractView />} />
              }
            />
          </Route>
        </Route>
        <Route path="admin" element={<Outlet />}>
          <Route index />
          <Route path="maker_checker_dashboard" element={<Outlet />}>
            <Route index element={<MakerCheckerView />} />
            <Route path="group" element={<Outlet />}>
              <Route index element={<GroupFormView />} />
              <Route path=":id" element={<GroupFormView />} />
            </Route>
          </Route>
          <Route
            path="merchant_settlement_dashboard"
            element={<MerchantSettlementView />}
          />
          <Route path="merchant_connect" element={<Outlet />}>
            <Route index />
            <Route path="my_merchant" element={<Outlet />}>
              <Route index />
              <Route path="merchants" element={<Outlet />}>
                <Route index element={<MyMerchantsView />} />
                <Route path=":id" element={<Outlet />}>
                  <Route index />
                  <Route
                    path="product_dashboard"
                    element={<ProductDashboardView />}
                  />
                  {!isID && (
                    <>
                      <Route path="payment_mode">
                        <Route index />
                        <Route
                          path=":mode"
                          element={<PaymentModeConfigurationFormView />}
                        />
                      </Route>
                      <Route path="application_fee">
                        <Route index />
                        <Route path="edit" element={<ApplicationFeeView />} />
                      </Route>
                    </>
                  )}
                  <Route path="ipp" element={<Outlet />}>
                    <Route path=":mode" element={<CreateUpdateIPPView />} />
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route path="merchants" element={<Outlet />}>
              <Route path=":id" element={<Outlet />}>
                <Route
                  path="payment_mode/:mode"
                  element={<PaymentModeConfigurationFormView />}
                />
                <Route
                  path="fave_passes/:mode"
                  element={<MerchantConnectFavePassForm />}
                />
                <Route
                  path="fave_passes/:fave_pass_id/:mode"
                  element={<MerchantConnectFavePassForm />}
                />
                <Route
                  path="loyalty_settings/:mode"
                  element={<StampCardForm />}
                />
                <Route
                  path="loyalty_settings/:loyaltySettingsId/:mode"
                  element={<StampCardForm />}
                />
                <Route
                  path="uob_point_redemptions/:mode"
                  element={<UOBPointRedemptionForm />}
                />
                <Route
                  path="uob_point_redemptions/:uob_point_redemption_id/:mode"
                  element={<UOBPointRedemptionForm />}
                />
              </Route>
            </Route>
            <Route path="my_submissions" element={<Outlet />}>
              <Route index element={<MySubmissionView />} />
              <Route path="favepay" element={<Outlet />}>
                <Route path=":id" element={<FavePaySubmissionsDetailsView />} />
              </Route>
              <Route path="deals" element={<Outlet />}>
                <Route
                  path=":id"
                  element={<DealsSubmissionsDetailsView />}
                ></Route>
              </Route>
              <Route path="ecards" element={<Outlet />}>
                <Route path=":id" element={<ECardSubmissionsDetailsView />} />
              </Route>
              <Route path="ipp" element={<Outlet />}>
                <Route
                  path=":id"
                  element={<InstallationPaymentPlanDetailsView />}
                ></Route>
              </Route>
              <Route path="payment_modes" element={<Outlet />}>
                <Route
                  path=":id"
                  element={<PaymentModeSubmissionsDetailsView />}
                ></Route>
              </Route>
              <Route path="fave_passes" element={<Outlet />}>
                <Route
                  path=":id"
                  element={<FavePassSubmissionsDetailsView />}
                ></Route>
              </Route>
              <Route path="loyalty_settings" element={<Outlet />}>
                <Route
                  path=":id"
                  element={<StampCardSubmissionsDetailsView />}
                ></Route>
              </Route>
            </Route>
          </Route>
          <Route path="partners" element={<Outlet />}>
            <Route path=":id" element={<Outlet />}>
              <Route path="payment_mode" element={<Outlet />}>
                <Route path="edit" element={<ApplicationPaymentModeView />} />
              </Route>
              <Route path="ipp" element={<Outlet />}>
                <Route path="edit" element={<IPPConfigurationView />} />
              </Route>
              <Route path="open_vouchers/:deal_id" element={<Outlet />}>
                <Route
                  path="outlet_settings"
                  element={
                    <WithIdParamValidation Component={<OutletSettingsView />} />
                  }
                />
              </Route>
              <Route path="finance" element={<Outlet />}>
                <Route path="bank_accounts" element={<Outlet />}>
                  <Route path=":bank_account_id" element={<Outlet />}>
                    <Route path="statements" element={<StatementsView />} />
                    <Route path="invoices" element={<InvoicesView />} />
                  </Route>
                </Route>
              </Route>
              <Route path="fave_passes" element={<Outlet />}>
                <Route index element={<FavePassesView />} />
                <Route path="create" element={<FavePassView />} />
                <Route path=":favePassId" element={<Outlet />}>
                  <Route path="edit" element={<FavePassView />} />
                  <Route
                    path="outlet_settings"
                    element={
                      <WithIdParamValidation
                        Component={<FavePassOutletSettingsView />}
                      />
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path="merchandise_and_campaign" element={<Outlet />}>
            <Route path="campaign_management_tool" element={<Outlet />}>
              <Route index element={<CampaignManagementView />} />
              <Route path="changelog/:id" element={<CampaignChangeLogView />} />
            </Route>
            <Route path="peeky_banners" element={<Outlet />}>
              <Route index element={<PeekyBannersView />} />
            </Route>
            <Route path="cashback_campaigns" element={<Outlet />}>
              <Route index element={<CashbackCampaignsView />} />
              <Route path="new_campaign" element={<NewCashbackCampaign />} />
              <Route
                path="edit_campaign/:id"
                element={<EditFaveCashbackCampaign />}
              />
              <Route
                path="edit_FLP_campaign/:id"
                element={<EditFLPCashbackCampaign />}
              />
              <Route
                path="edit_ecard_campaign/:id"
                element={<EditEcardCashbackCampaign />}
              />
              <Route
                path="changelog/:id"
                element={<CashbackCampaignChangeLogView />}
              />
            </Route>
          </Route>
        </Route>
        <Route path="settings" element={<Outlet />}>
          <Route path="nature_of_business" element={<Outlet />}>
            <Route index element={<NatureOfBusinessView />} />
            <Route path="new" element={<CreateNatureOfBusinessView />} />
            <Route path=":id" element={<CreateNatureOfBusinessView />} />
          </Route>
        </Route>
        <Route path="fave_arcade" element={<Outlet />}>
          <Route path="ticket_center" element={<Outlet />}>
            <Route index element={<TicketCenterView />} />
            <Route path="new" element={<CreateNewCampaignView />} />
            <Route path="changelog/:id" element={<TicketChangelogView />} />
            <Route path="view_campaign/:id" element={<TicketDetailsView />} />
            <Route path="edit_campaign/:id" element={<TicketEditView />} />
          </Route>
          <Route path="games_center" element={<Outlet />}>
            <Route index element={<GamesCenterView />} />
            <Route path="new" element={<CreateNewGameCampaignView />} />
            <Route path="changelog/:id" element={<GameChangelogView />} />
            <Route path="view_campaign/:id" element={<GameDetailsView />} />
            <Route
              path="edit_campaign/:id"
              element={<GameCampaignEditView />}
            />
          </Route>
          <Route path="prize_catalog" element={<Outlet />}>
            <Route index element={<PrizeCenterView />} />
            <Route path="new" element={<CreateNewPrizeView />} />
            <Route
              path="changelog/:id"
              element={<PrizeCampaignChangelogView />}
            />
            <Route path="view_campaign/:id" element={<PrizeDetailsView />} />
            <Route path="edit_campaign/:id" element={<EditPrizeCampaign />} />
          </Route>
        </Route>
        <Route path="engineering" element={<Outlet />}>
          <Route path="manual_refund" element={<ManualRefund />} />
        </Route>
        {/* no page found */}
        <Route path="*" element={<Empty emptyType={'cant-find-anything'} />} />
      </Route>
      <Route
        path={'/login'}
        element={
          <RedirectToAuthenticatedPagesView>
            <LoginFormView />
          </RedirectToAuthenticatedPagesView>
        }
      />
      <Route
        path="/auth/microsoft_graph/callback"
        element={<MicrosoftCallbackView />}
      />
      {/* accessible only in staging and localhost environments */}
      {isStaging && (
        <Route path="forgot_password" element={<ForgotPasswordFormView />} />
      )}
    </Routes>
  );
};
